import { Card } from "@repo/components/src/ui/card";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Icon } from "@repo/components/src/ui/icon";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { AlertCircleIcon, LucideIcon } from "lucide-react-native";
import { useCallback } from "react";
import { Controller, FieldValues } from "react-hook-form";
import { Box } from "../../ui/box";
import { HStack } from "../../ui/hstack";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputProps } from "../types";

type FormInputSelectIconsProps<T extends FieldValues, V> = FormInputProps<
  T,
  V
> & {
  options: {
    value: string;
    title: string;
    label?: string;
    Icon?: LucideIcon;
    lastComponent?: React.ReactNode;
  }[];
};

type Props<TFieldValues extends FieldValues> =
  | ({
      multiple: true;
    } & Omit<
      FormInputSelectIconsProps<TFieldValues, string[] | undefined>,
      "placeholder"
    >)
  | ({
      multiple: false;
    } & Omit<
      FormInputSelectIconsProps<TFieldValues, string | null | undefined>,
      "placeholder"
    >);

export default function FormInputSelectIcons<TFieldValues extends FieldValues>(
  props: Props<TFieldValues>,
) {
  const {
    control,
    name,
    label,
    required,
    disabled: disabledProp = false,
    className,
    size,
    options,
    labelSize,
    multiple,
  } = props;
  const buttonOnChange = useCallback(
    (
      originalOnChange: (...event: any[]) => void,
      buttonValue: string,
      actualValue?: string[],
    ) =>
      () => {
        if (multiple) {
          if (actualValue?.includes(buttonValue)) {
            originalOnChange(actualValue.filter((a) => a !== buttonValue));
          } else {
            originalOnChange([...(actualValue || []), buttonValue]);
          }
        } else {
          originalOnChange(buttonValue);
        }
      },
    [multiple],
  );
  const getIsSelected = useCallback(
    (value: string | string[], option: string) => {
      if (multiple) return value.includes(option);
      return value === option;
    },
    [multiple],
  );
  return (
    <Controller
      name={name}
      control={control}
      disabled={disabledProp}
      render={({
        field: { onChange, value, ref, disabled },
        fieldState: { error },
      }) => (
        <FormControl
          isInvalid={!!error}
          isRequired={required}
          isDisabled={disabled}
          className={className}
          size={size}
        >
          {!!label && (
            <FormControlLabel>
              <FormControlLabelText size={labelSize || size}>
                {label}
              </FormControlLabelText>
            </FormControlLabel>
          )}
          <HStack flexSpace="md" ref={focusScrollRef(ref)}>
            {options.map((option) => {
              const isSelected = getIsSelected(value, option.value);
              return (
                <Box className="w-1/3" key={option.value}>
                  <Pressable
                    onPress={buttonOnChange(onChange, option.value, value)}
                    className="h-full"
                    disabled={disabled}
                  >
                    {(pressableProps: any) => (
                      <Card
                        variant="filled"
                        size="md"
                        className={`h-full rounded-2xl border-2 border-transparent ${isSelected ? "bg-brand-50 border-brand-400" : ""} ${pressableProps.pressed ? "opacity-70" : ""}`}
                      >
                        <HStack space="sm" className="items-start">
                          {option.Icon ? (
                            <Icon
                              as={option.Icon}
                              className={
                                isSelected ? "text-brand-400 mt-1" : "mt-1"
                              }
                            />
                          ) : (
                            <></>
                          )}
                          <Text
                            size="lg"
                            className={
                              isSelected
                                ? "text-brand-400 flex-1 shrink"
                                : "flex-1 shrink"
                            }
                          >
                            {option.title}
                          </Text>
                        </HStack>
                        {!!option.label && (
                          <Box className="mt-2">
                            <Text
                              size="sm"
                              className={isSelected ? "text-brand-400" : ""}
                            >
                              {option.label}
                            </Text>
                          </Box>
                        )}
                        {option.lastComponent}
                      </Card>
                    )}
                  </Pressable>
                </Box>
              );
            })}
          </HStack>
          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
            <FormControlErrorText>{error?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  );
}
